import React, {ReactNode} from 'react'
import styled from 'styled-components'
import type {Getter} from '../../../../../common/types'
import SVGRemove from '../../../../../assets/icons/remove-small.svg'
import {Input} from '../../../components/library/input'
import {ScriptInput} from '../../../components/library/input/script-input'
import {Select} from '../../../components/library/select'
import {Fields} from '../../../components/properties'
import {Button} from '../../../components/library/button'

const RemoveWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 10px 0px;
`

interface PropertyEditorProps {
  name?: string
  updateName?: (name: string) => void
  getter: Getter
  update: (obj: Getter) => void
  remove?: () => void
  pathOptions: string[]
  readOnly?: boolean
  renderStaticInputElement?: (
    value: unknown,
    onChange: (val?: unknown) => void
  ) => ReactNode
  context?: Record<PropertyKey, unknown>
  scriptTheme?: 'light' | 'atomic'
}

export const PropertyEditor = ({
  name,
  updateName,
  getter,
  update,
  pathOptions,
  readOnly,
  remove,
  renderStaticInputElement,
  context,
  scriptTheme
}: PropertyEditorProps) => {
  return (
    <>
      <Fields>
        {updateName ? (
          <Input
            label="Name"
            value={name}
            onChange={value => {
              updateName(value)
            }}
            readOnly={readOnly}
          />
        ) : null}
        <Select
          label="Type"
          value={getter.type ?? 'path'}
          onChange={type => {
            switch (type) {
              case 'script':
                return update({
                  script: '',
                  type: 'script'
                })
              case 'path':
                return update({
                  path: '',
                  type: 'path'
                })
              default:
                update({
                  value: '',
                  type: 'static'
                })
            }
          }}
          readOnly={readOnly}
        >
          {['path', 'script', 'static'].map(type => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </Select>

        {getter.type === 'script' && (
          <ScriptInput
            label="Script"
            context={context}
            value={getter.script}
            onChange={script => {
              update({
                ...getter,
                script
              })
            }}
            readOnly={readOnly}
            theme={scriptTheme}
          />
        )}

        {(!getter.type || getter.type === 'path') && (
          <Select
            label="Path"
            value={getter.path}
            onChange={value => {
              const path = value ?? ''
              update({
                ...getter,
                path
              })
            }}
            readOnly={readOnly}
          >
            {
              // Show a disabled option for specified paths that don't exist in pathOptions
              !pathOptions.includes(getter.path) && (
                <option disabled value={getter.path}>
                  {getter.path}
                </option>
              )
            }
            {pathOptions.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        )}

        {getter.type === 'static' && (
          <>
            {renderStaticInputElement ? (
              renderStaticInputElement(getter.value, value => {
                update({
                  ...getter,
                  value
                })
              })
            ) : (
              <Input
                label="Value"
                // TODO in theory this can be anything
                value={String(getter.value)}
                onChange={value => {
                  update({
                    ...getter,
                    value
                  })
                }}
                readOnly={readOnly}
              />
            )}
          </>
        )}
      </Fields>
      {remove && (
        <RemoveWrap>
          <Button
            disabled={readOnly}
            appearance="transparent"
            onClick={remove}
            isRounded={true}
            style={{width: '24px', height: '24px', padding: 0}}
          >
            <SVGRemove />
          </Button>
        </RemoveWrap>
      )}
    </>
  )
}
